/**
 * Policy Routing Engine (PRE) Zustand Store
 *
 * Manages all state for the Policy Routing Engine including:
 * - Jurisdictions, Rules, Calling Hours, DNC Registries, Frequency Caps
 * - Decision Logs with filters
 * - AI Classifications with filters
 * - CRUD operations with mock data fallback
 */
import { create } from 'zustand';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import type {
  Jurisdiction,
  CreateJurisdictionDto,
  UpdateJurisdictionDto,
  Rule,
  CreateRuleDto,
  UpdateRuleDto,
  CallingHourWindow,
  CreateCallingHourWindowDto,
  UpdateCallingHourWindowDto,
  DNCRegistry,
  CreateDncRegistryDto,
  UpdateDncRegistryDto,
  FrequencyCapConfig,
  CreateFrequencyCapConfigDto,
  UpdateFrequencyCapConfigDto,
  PREDecisionLog,
  PREDecisionLogFilters,
  PREAiClassification,
  PREAiClassificationFilters,
  PRECheckDefinition,
  CreatePRECheckDefinitionDto,
  UpdatePRECheckDefinitionDto,
  MessagePurpose,
  PREChannel,
  RuleLayer,
  PaginationMeta,
} from '@respectly/shared-types';
import {
  PRE_JURISDICTIONS,
  PRE_RULES,
  PRE_CALLING_HOUR_WINDOWS,
  PRE_DNC_REGISTRIES,
  PRE_FREQUENCY_CAPS,
  MOCK_DECISION_LOGS,
  MOCK_AI_CLASSIFICATIONS,
  PRE_CHECK_DEFINITIONS,
} from '../data/pre-rules';

// ============================================
// Types
// ============================================

interface PolicyError {
  code: string;
  message: string;
}

interface DecisionStats {
  total: number;
  byDecision: { decision: string; count: number }[];
  byChannel: { channel: string; count: number }[];
  byJurisdiction: { jurisdictionCode: string; count: number }[];
  trend: { date: string; ALLOW: number; ALLOW_WITH_CONDITIONS: number; BLOCK: number }[];
}

interface AiClassificationStats {
  total: number;
  byPurpose: { purpose: string; count: number }[];
  flaggedCount: number;
  overrideCount: number;
  confidenceDistribution: Record<string, number>;
}

interface RulesFilter {
  search?: string;
  jurisdictionCode?: string;
  channel?: PREChannel;
  messagePurpose?: MessagePurpose;
  layer?: RuleLayer;
  isActive?: boolean;
}

// ============================================
// State Interface
// ============================================

export interface PolicyState {
  // Jurisdictions
  jurisdictions: Jurisdiction[];
  isLoadingJurisdictions: boolean;
  jurisdictionsError: PolicyError | null;

  // Rules
  rules: Rule[];
  rulesPagination: PaginationMeta | null;
  rulesLimit: number;
  isLoadingRules: boolean;
  rulesError: PolicyError | null;
  rulesFilter: RulesFilter;

  // Active Rule
  activeRule: Rule | null;
  isLoadingRule: boolean;

  // Calling Hours
  callingHours: CallingHourWindow[];
  isLoadingCallingHours: boolean;
  callingHoursError: PolicyError | null;

  // DNC Registries
  dncRegistries: DNCRegistry[];
  isLoadingDncRegistries: boolean;
  dncRegistriesError: PolicyError | null;

  // Frequency Caps
  frequencyCaps: FrequencyCapConfig[];
  isLoadingFrequencyCaps: boolean;
  frequencyCapsError: PolicyError | null;

  // Check Definitions (catalogue of canonical PRE checks)
  checkDefinitions: PRECheckDefinition[];
  isLoadingCheckDefinitions: boolean;
  checkDefinitionsError: PolicyError | null;

  // Decision Logs
  decisionLogs: PREDecisionLog[];
  decisionLogsPagination: PaginationMeta | null;
  isLoadingDecisionLogs: boolean;
  decisionLogsError: PolicyError | null;
  decisionLogFilters: PREDecisionLogFilters;

  // Active Decision Log
  activeDecisionLog: PREDecisionLog | null;
  isLoadingDecisionLog: boolean;

  // AI Classifications
  aiClassifications: PREAiClassification[];
  aiClassificationsPagination: PaginationMeta | null;
  isLoadingAiClassifications: boolean;
  aiClassificationsError: PolicyError | null;
  aiClassificationFilters: PREAiClassificationFilters;

  // Action States
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isTogglingRule: string | null;
  actionError: PolicyError | null;

  // Jurisdiction Actions
  loadJurisdictions: () => Promise<void>;
  createJurisdiction: (dto: CreateJurisdictionDto) => Promise<string | null>;
  updateJurisdiction: (id: string, dto: UpdateJurisdictionDto) => Promise<boolean>;
  deleteJurisdiction: (id: string) => Promise<boolean>;

  // Rule Actions
  /**
   * Loads a page of rules from the API.
   * @param page  1-indexed page number (default 1).
   * @param limit Page size. Default 20 for paginated CRUD views; the policy
   *              authoring page passes a large value to fetch the whole rule
   *              set in one request so inherited country/state rules are
   *              actually visible.
   */
  loadRules: (page?: number, limit?: number) => Promise<void>;
  refreshRules: () => Promise<void>;
  loadRule: (id: string) => Promise<void>;
  createRule: (dto: CreateRuleDto) => Promise<string | null>;
  updateRule: (id: string, dto: UpdateRuleDto) => Promise<boolean>;
  deleteRule: (id: string) => Promise<boolean>;
  toggleRule: (id: string, isActive: boolean) => Promise<boolean>;
  setRuleFilter: <K extends keyof RulesFilter>(key: K, value: RulesFilter[K]) => void;
  clearRuleFilters: () => void;

  // Calling Hour Actions
  loadCallingHours: () => Promise<void>;
  createCallingHourWindow: (dto: CreateCallingHourWindowDto) => Promise<string | null>;
  updateCallingHourWindow: (id: string, dto: UpdateCallingHourWindowDto) => Promise<boolean>;
  deleteCallingHourWindow: (id: string) => Promise<boolean>;

  // DNC Registry Actions
  loadDncRegistries: () => Promise<void>;
  createDncRegistry: (dto: CreateDncRegistryDto) => Promise<string | null>;
  updateDncRegistry: (id: string, dto: UpdateDncRegistryDto) => Promise<boolean>;
  deleteDncRegistry: (id: string) => Promise<boolean>;

  // Frequency Cap Actions
  loadFrequencyCaps: () => Promise<void>;
  createFrequencyCap: (dto: CreateFrequencyCapConfigDto) => Promise<string | null>;
  updateFrequencyCap: (id: string, dto: UpdateFrequencyCapConfigDto) => Promise<boolean>;
  deleteFrequencyCap: (id: string) => Promise<boolean>;

  // Check Definition Actions
  loadCheckDefinitions: () => Promise<void>;
  createCheckDefinition: (dto: CreatePRECheckDefinitionDto) => Promise<string | null>;
  updateCheckDefinition: (id: string, dto: UpdatePRECheckDefinitionDto) => Promise<boolean>;
  deleteCheckDefinition: (id: string) => Promise<boolean>;

  // Decision Log Actions
  loadDecisionLogs: (page?: number) => Promise<void>;
  loadDecisionLog: (id: string) => Promise<void>;
  setDecisionLogFilter: <K extends keyof PREDecisionLogFilters>(key: K, value: PREDecisionLogFilters[K]) => void;
  clearDecisionLogFilters: () => void;

  // AI Classification Actions
  loadAiClassifications: (page?: number) => Promise<void>;
  overrideAiClassification: (id: string, purpose: MessagePurpose) => Promise<boolean>;
  setAiClassificationFilter: <K extends keyof PREAiClassificationFilters>(key: K, value: PREAiClassificationFilters[K]) => void;
  clearAiClassificationFilters: () => void;

  // Import/Export Actions
  importRules: (rules: CreateRuleDto[]) => Promise<{ imported: number; failed: number } | null>;
  exportRules: (jurisdictionCode?: string) => Promise<Rule[]>;

  // Stats Actions
  decisionStats: DecisionStats | null;
  isLoadingDecisionStats: boolean;
  loadDecisionStats: () => Promise<void>;
  aiStats: AiClassificationStats | null;
  isLoadingAiStats: boolean;
  loadAiStats: () => Promise<void>;

  // Utility
  clearError: () => void;
  reset: () => void;
}

// ============================================
// Initial State
// ============================================

const getInitialState = () => ({
  // Jurisdictions
  jurisdictions: [] as Jurisdiction[],
  isLoadingJurisdictions: false,
  jurisdictionsError: null as PolicyError | null,

  // Rules
  rules: [] as Rule[],
  rulesPagination: null as PaginationMeta | null,
  rulesLimit: 20,
  isLoadingRules: false,
  rulesError: null as PolicyError | null,
  rulesFilter: {} as RulesFilter,

  // Active Rule
  activeRule: null as Rule | null,
  isLoadingRule: false,

  // Calling Hours
  callingHours: [] as CallingHourWindow[],
  isLoadingCallingHours: false,
  callingHoursError: null as PolicyError | null,

  // DNC Registries
  dncRegistries: [] as DNCRegistry[],
  isLoadingDncRegistries: false,
  dncRegistriesError: null as PolicyError | null,

  // Frequency Caps
  frequencyCaps: [] as FrequencyCapConfig[],
  isLoadingFrequencyCaps: false,
  frequencyCapsError: null as PolicyError | null,

  // Check Definitions
  checkDefinitions: [] as PRECheckDefinition[],
  isLoadingCheckDefinitions: false,
  checkDefinitionsError: null as PolicyError | null,

  // Decision Logs
  decisionLogs: [] as PREDecisionLog[],
  decisionLogsPagination: null as PaginationMeta | null,
  isLoadingDecisionLogs: false,
  decisionLogsError: null as PolicyError | null,
  decisionLogFilters: {} as PREDecisionLogFilters,

  // Active Decision Log
  activeDecisionLog: null as PREDecisionLog | null,
  isLoadingDecisionLog: false,

  // AI Classifications
  aiClassifications: [] as PREAiClassification[],
  aiClassificationsPagination: null as PaginationMeta | null,
  isLoadingAiClassifications: false,
  aiClassificationsError: null as PolicyError | null,
  aiClassificationFilters: {} as PREAiClassificationFilters,

  // Stats
  decisionStats: null as DecisionStats | null,
  isLoadingDecisionStats: false,
  aiStats: null as AiClassificationStats | null,
  isLoadingAiStats: false,

  // Action States
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  isTogglingRule: null as string | null,
  actionError: null as PolicyError | null,
});

// ============================================
// Helper: mock pagination
// ============================================

function mockPagination(total: number, page = 1, limit = 20): PaginationMeta {
  return {
    page,
    limit,
    total,
    totalPages: Math.ceil(total / limit),
    sortOrder: 'desc' as const,
  };
}

// ============================================
// Store Implementation
// ============================================

export const usePolicyStore = create<PolicyState>((set, get) => ({
  ...getInitialState(),

  // ============================================
  // Jurisdiction Actions
  // ============================================

  loadJurisdictions: async () => {
    set({ isLoadingJurisdictions: true, jurisdictionsError: null });
    try {
      const api = getApiClient();
      const response = await api.get<Jurisdiction[]>(API_ENDPOINTS.SUPER_ADMIN.PRE.JURISDICTIONS.LIST);
      set({
        jurisdictions: response,
        isLoadingJurisdictions: false,
      });
    } catch {
      set({
        jurisdictions: [...PRE_JURISDICTIONS],
        isLoadingJurisdictions: false,
      });
    }
  },

  createJurisdiction: async (dto: CreateJurisdictionDto): Promise<string | null> => {
    set({ isCreating: true, actionError: null });
    try {
      const api = getApiClient();
      const response = await api.post<Jurisdiction>(API_ENDPOINTS.SUPER_ADMIN.PRE.JURISDICTIONS.CREATE, dto);
      set({ isCreating: false });
      await get().loadJurisdictions();
      return response.id;
    } catch (error) {
      set({
        isCreating: false,
        actionError: {
          code: 'CREATE_JURISDICTION_FAILED',
          message: error instanceof Error ? error.message : 'Failed to create jurisdiction',
        },
      });
      return null;
    }
  },

  updateJurisdiction: async (id: string, dto: UpdateJurisdictionDto): Promise<boolean> => {
    set({ isUpdating: true, actionError: null });
    try {
      const api = getApiClient();
      await api.put(API_ENDPOINTS.SUPER_ADMIN.PRE.JURISDICTIONS.UPDATE(id), dto);
      set({ isUpdating: false });
      await get().loadJurisdictions();
      return true;
    } catch (error) {
      set({
        isUpdating: false,
        actionError: {
          code: 'UPDATE_JURISDICTION_FAILED',
          message: error instanceof Error ? error.message : 'Failed to update jurisdiction',
        },
      });
      return false;
    }
  },

  deleteJurisdiction: async (id: string): Promise<boolean> => {
    set({ isDeleting: true, actionError: null });
    try {
      const api = getApiClient();
      await api.delete(API_ENDPOINTS.SUPER_ADMIN.PRE.JURISDICTIONS.DELETE(id));
      set((state) => ({
        jurisdictions: state.jurisdictions.filter((j) => j.id !== id),
        isDeleting: false,
      }));
      return true;
    } catch (error) {
      set({
        isDeleting: false,
        actionError: {
          code: 'DELETE_JURISDICTION_FAILED',
          message: error instanceof Error ? error.message : 'Failed to delete jurisdiction',
        },
      });
      return false;
    }
  },

  // ============================================
  // Rule Actions
  // ============================================

  loadRules: async (page = 1, limit = 20) => {
    set({ isLoadingRules: true, rulesError: null });
    try {
      const api = getApiClient();
      const { rulesFilter } = get();

      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', limit.toString());
      if (rulesFilter.search) params.set('search', rulesFilter.search);
      if (rulesFilter.jurisdictionCode) params.set('jurisdictionCode', rulesFilter.jurisdictionCode);
      if (rulesFilter.channel) params.set('channel', rulesFilter.channel);
      if (rulesFilter.messagePurpose) params.set('messagePurpose', rulesFilter.messagePurpose);
      if (rulesFilter.layer) params.set('layer', rulesFilter.layer);
      if (rulesFilter.isActive !== undefined) params.set('isActive', String(rulesFilter.isActive));

      const response = await api.get<{ data: Rule[]; pagination: PaginationMeta }>(
        `${API_ENDPOINTS.SUPER_ADMIN.PRE.RULES.LIST}?${params.toString()}`
      );

      set({
        rules: response.data,
        rulesPagination: response.pagination,
        rulesLimit: limit,
        isLoadingRules: false,
      });
    } catch {
      // Fallback to mock data with client-side filtering
      const { rulesFilter } = get();
      let filtered = [...PRE_RULES];

      if (rulesFilter.search) {
        const s = rulesFilter.search.toLowerCase();
        filtered = filtered.filter(
          (r) =>
            r.name.toLowerCase().includes(s) ||
            r.ruleId.toLowerCase().includes(s) ||
            r.description.toLowerCase().includes(s)
        );
      }
      if (rulesFilter.jurisdictionCode) {
        filtered = filtered.filter((r) => r.jurisdictionCode === rulesFilter.jurisdictionCode);
      }
      if (rulesFilter.channel) {
        filtered = filtered.filter((r) => r.channel === rulesFilter.channel || r.channel === null);
      }
      if (rulesFilter.messagePurpose) {
        filtered = filtered.filter((r) => r.messagePurpose === rulesFilter.messagePurpose || r.messagePurpose === null);
      }
      if (rulesFilter.layer) {
        filtered = filtered.filter((r) => r.layer === rulesFilter.layer);
      }
      if (rulesFilter.isActive !== undefined) {
        filtered = filtered.filter((r) => r.isActive === rulesFilter.isActive);
      }

      set({
        rules: filtered,
        rulesPagination: mockPagination(filtered.length, page),
        rulesLimit: limit,
        isLoadingRules: false,
      });
    }
  },

  refreshRules: async () => {
    const { rulesPagination, rulesLimit } = get();
    await get().loadRules(rulesPagination?.page ?? 1, rulesLimit);
  },

  loadRule: async (id: string) => {
    set({ isLoadingRule: true, actionError: null });
    try {
      const api = getApiClient();
      const response = await api.get<Rule>(API_ENDPOINTS.SUPER_ADMIN.PRE.RULES.GET(id));
      set({ activeRule: response, isLoadingRule: false });
    } catch {
      const mockRule = PRE_RULES.find((r) => r.id === id) ?? null;
      set({ activeRule: mockRule, isLoadingRule: false });
    }
  },

  createRule: async (dto: CreateRuleDto): Promise<string | null> => {
    set({ isCreating: true, actionError: null });
    try {
      const api = getApiClient();
      const response = await api.post<Rule>(API_ENDPOINTS.SUPER_ADMIN.PRE.RULES.CREATE, dto);
      set({ isCreating: false });
      await get().refreshRules();
      return response.id;
    } catch (error) {
      set({
        isCreating: false,
        actionError: {
          code: 'CREATE_RULE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to create rule',
        },
      });
      return null;
    }
  },

  updateRule: async (id: string, dto: UpdateRuleDto): Promise<boolean> => {
    set({ isUpdating: true, actionError: null });
    try {
      const api = getApiClient();
      await api.put(API_ENDPOINTS.SUPER_ADMIN.PRE.RULES.UPDATE(id), dto);
      set({ isUpdating: false });
      await get().refreshRules();
      return true;
    } catch (error) {
      set({
        isUpdating: false,
        actionError: {
          code: 'UPDATE_RULE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to update rule',
        },
      });
      return false;
    }
  },

  deleteRule: async (id: string): Promise<boolean> => {
    set({ isDeleting: true, actionError: null });
    try {
      const api = getApiClient();
      await api.delete(API_ENDPOINTS.SUPER_ADMIN.PRE.RULES.DELETE(id));
      set((state) => ({
        rules: state.rules.filter((r) => r.id !== id),
        isDeleting: false,
      }));
      return true;
    } catch (error) {
      set({
        isDeleting: false,
        actionError: {
          code: 'DELETE_RULE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to delete rule',
        },
      });
      return false;
    }
  },

  toggleRule: async (id: string, isActive: boolean): Promise<boolean> => {
    set({ isTogglingRule: id, actionError: null });

    // Optimistic update
    set((state) => ({
      rules: state.rules.map((r) => (r.id === id ? { ...r, isActive } : r)),
    }));

    try {
      const api = getApiClient();
      const endpoint = isActive
        ? API_ENDPOINTS.SUPER_ADMIN.PRE.RULES.ACTIVATE(id)
        : API_ENDPOINTS.SUPER_ADMIN.PRE.RULES.DEACTIVATE(id);
      await api.patch(endpoint, { isActive });
      set({ isTogglingRule: null });
      return true;
    } catch (error) {
      // Revert optimistic update
      set((state) => ({
        rules: state.rules.map((r) => (r.id === id ? { ...r, isActive: !isActive } : r)),
        isTogglingRule: null,
        actionError: {
          code: 'TOGGLE_RULE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to toggle rule',
        },
      }));
      return false;
    }
  },

  setRuleFilter: (key, value) => {
    set((state) => ({
      rulesFilter: { ...state.rulesFilter, [key]: value },
    }));
    get().loadRules(1);
  },

  clearRuleFilters: () => {
    set({ rulesFilter: {} });
    get().loadRules(1);
  },

  // ============================================
  // Calling Hour Actions
  // ============================================

  loadCallingHours: async () => {
    set({ isLoadingCallingHours: true, callingHoursError: null });
    try {
      const api = getApiClient();
      const response = await api.get<CallingHourWindow[]>(API_ENDPOINTS.SUPER_ADMIN.PRE.CALLING_HOURS.LIST);
      set({
        callingHours: response,
        isLoadingCallingHours: false,
      });
    } catch {
      set({
        callingHours: [...PRE_CALLING_HOUR_WINDOWS],
        isLoadingCallingHours: false,
      });
    }
  },

  createCallingHourWindow: async (dto: CreateCallingHourWindowDto): Promise<string | null> => {
    set({ isCreating: true, actionError: null });
    try {
      const api = getApiClient();
      const response = await api.post<CallingHourWindow>(API_ENDPOINTS.SUPER_ADMIN.PRE.CALLING_HOURS.CREATE, dto);
      set({ isCreating: false });
      await get().loadCallingHours();
      return response.id;
    } catch (error) {
      set({
        isCreating: false,
        actionError: {
          code: 'CREATE_CALLING_HOUR_FAILED',
          message: error instanceof Error ? error.message : 'Failed to create calling hour window',
        },
      });
      return null;
    }
  },

  updateCallingHourWindow: async (id: string, dto: UpdateCallingHourWindowDto): Promise<boolean> => {
    set({ isUpdating: true, actionError: null });
    try {
      const api = getApiClient();
      await api.put(API_ENDPOINTS.SUPER_ADMIN.PRE.CALLING_HOURS.UPDATE(id), dto);
      set({ isUpdating: false });
      await get().loadCallingHours();
      return true;
    } catch (error) {
      set({
        isUpdating: false,
        actionError: {
          code: 'UPDATE_CALLING_HOUR_FAILED',
          message: error instanceof Error ? error.message : 'Failed to update calling hour window',
        },
      });
      return false;
    }
  },

  deleteCallingHourWindow: async (id: string): Promise<boolean> => {
    set({ isDeleting: true, actionError: null });
    try {
      const api = getApiClient();
      await api.delete(API_ENDPOINTS.SUPER_ADMIN.PRE.CALLING_HOURS.DELETE(id));
      set((state) => ({
        callingHours: state.callingHours.filter((w) => w.id !== id),
        isDeleting: false,
      }));
      return true;
    } catch (error) {
      set({
        isDeleting: false,
        actionError: {
          code: 'DELETE_CALLING_HOUR_FAILED',
          message: error instanceof Error ? error.message : 'Failed to delete calling hour window',
        },
      });
      return false;
    }
  },

  // ============================================
  // DNC Registry Actions
  // ============================================

  loadDncRegistries: async () => {
    set({ isLoadingDncRegistries: true, dncRegistriesError: null });
    try {
      const api = getApiClient();
      const response = await api.get<DNCRegistry[]>(API_ENDPOINTS.SUPER_ADMIN.PRE.DNC.LIST);
      set({
        dncRegistries: response,
        isLoadingDncRegistries: false,
      });
    } catch {
      set({
        dncRegistries: [...PRE_DNC_REGISTRIES],
        isLoadingDncRegistries: false,
      });
    }
  },

  createDncRegistry: async (dto: CreateDncRegistryDto): Promise<string | null> => {
    set({ isCreating: true, actionError: null });
    try {
      const api = getApiClient();
      const response = await api.post<DNCRegistry>(API_ENDPOINTS.SUPER_ADMIN.PRE.DNC.CREATE, dto);
      set({ isCreating: false });
      await get().loadDncRegistries();
      return response.id;
    } catch (error) {
      set({
        isCreating: false,
        actionError: {
          code: 'CREATE_DNC_REGISTRY_FAILED',
          message: error instanceof Error ? error.message : 'Failed to create DNC registry',
        },
      });
      return null;
    }
  },

  updateDncRegistry: async (id: string, dto: UpdateDncRegistryDto): Promise<boolean> => {
    set({ isUpdating: true, actionError: null });
    try {
      const api = getApiClient();
      await api.put(API_ENDPOINTS.SUPER_ADMIN.PRE.DNC.UPDATE(id), dto);
      set({ isUpdating: false });
      await get().loadDncRegistries();
      return true;
    } catch (error) {
      set({
        isUpdating: false,
        actionError: {
          code: 'UPDATE_DNC_REGISTRY_FAILED',
          message: error instanceof Error ? error.message : 'Failed to update DNC registry',
        },
      });
      return false;
    }
  },

  deleteDncRegistry: async (id: string): Promise<boolean> => {
    set({ isDeleting: true, actionError: null });
    try {
      const api = getApiClient();
      await api.delete(API_ENDPOINTS.SUPER_ADMIN.PRE.DNC.DELETE(id));
      set((state) => ({
        dncRegistries: state.dncRegistries.filter((r) => r.id !== id),
        isDeleting: false,
      }));
      return true;
    } catch (error) {
      set({
        isDeleting: false,
        actionError: {
          code: 'DELETE_DNC_REGISTRY_FAILED',
          message: error instanceof Error ? error.message : 'Failed to delete DNC registry',
        },
      });
      return false;
    }
  },

  // ============================================
  // Frequency Cap Actions
  // ============================================

  loadFrequencyCaps: async () => {
    set({ isLoadingFrequencyCaps: true, frequencyCapsError: null });
    try {
      const api = getApiClient();
      const response = await api.get<FrequencyCapConfig[]>(API_ENDPOINTS.SUPER_ADMIN.PRE.FREQUENCY_CAPS.LIST);
      set({
        frequencyCaps: response,
        isLoadingFrequencyCaps: false,
      });
    } catch {
      set({
        frequencyCaps: [...PRE_FREQUENCY_CAPS],
        isLoadingFrequencyCaps: false,
      });
    }
  },

  createFrequencyCap: async (dto: CreateFrequencyCapConfigDto): Promise<string | null> => {
    set({ isCreating: true, actionError: null });
    try {
      const api = getApiClient();
      const response = await api.post<FrequencyCapConfig>(API_ENDPOINTS.SUPER_ADMIN.PRE.FREQUENCY_CAPS.CREATE, dto);
      set({ isCreating: false });
      await get().loadFrequencyCaps();
      return response.id;
    } catch (error) {
      set({
        isCreating: false,
        actionError: {
          code: 'CREATE_FREQUENCY_CAP_FAILED',
          message: error instanceof Error ? error.message : 'Failed to create frequency cap',
        },
      });
      return null;
    }
  },

  updateFrequencyCap: async (id: string, dto: UpdateFrequencyCapConfigDto): Promise<boolean> => {
    set({ isUpdating: true, actionError: null });
    try {
      const api = getApiClient();
      await api.put(API_ENDPOINTS.SUPER_ADMIN.PRE.FREQUENCY_CAPS.UPDATE(id), dto);
      set({ isUpdating: false });
      await get().loadFrequencyCaps();
      return true;
    } catch (error) {
      set({
        isUpdating: false,
        actionError: {
          code: 'UPDATE_FREQUENCY_CAP_FAILED',
          message: error instanceof Error ? error.message : 'Failed to update frequency cap',
        },
      });
      return false;
    }
  },

  deleteFrequencyCap: async (id: string): Promise<boolean> => {
    set({ isDeleting: true, actionError: null });
    try {
      const api = getApiClient();
      await api.delete(API_ENDPOINTS.SUPER_ADMIN.PRE.FREQUENCY_CAPS.DELETE(id));
      set((state) => ({
        frequencyCaps: state.frequencyCaps.filter((c) => c.id !== id),
        isDeleting: false,
      }));
      return true;
    } catch (error) {
      set({
        isDeleting: false,
        actionError: {
          code: 'DELETE_FREQUENCY_CAP_FAILED',
          message: error instanceof Error ? error.message : 'Failed to delete frequency cap',
        },
      });
      return false;
    }
  },

  // ============================================
  // Check Definition Actions
  // ============================================

  loadCheckDefinitions: async () => {
    set({ isLoadingCheckDefinitions: true, checkDefinitionsError: null });
    try {
      const api = getApiClient();
      const response = await api.get<PRECheckDefinition[]>(
        API_ENDPOINTS.SUPER_ADMIN.PRE.CHECK_DEFINITIONS.LIST
      );
      set({ checkDefinitions: response, isLoadingCheckDefinitions: false });
    } catch {
      set({ checkDefinitions: [...PRE_CHECK_DEFINITIONS], isLoadingCheckDefinitions: false });
    }
  },

  createCheckDefinition: async (dto: CreatePRECheckDefinitionDto): Promise<string | null> => {
    set({ isCreating: true, actionError: null });
    try {
      const api = getApiClient();
      const response = await api.post<PRECheckDefinition>(
        API_ENDPOINTS.SUPER_ADMIN.PRE.CHECK_DEFINITIONS.CREATE,
        dto
      );
      set({ isCreating: false });
      await get().loadCheckDefinitions();
      return response.id ?? null;
    } catch (error) {
      set({
        isCreating: false,
        actionError: {
          code: 'CREATE_CHECK_DEFINITION_FAILED',
          message: error instanceof Error ? error.message : 'Failed to create check definition',
        },
      });
      return null;
    }
  },

  updateCheckDefinition: async (id: string, dto: UpdatePRECheckDefinitionDto): Promise<boolean> => {
    set({ isUpdating: true, actionError: null });
    try {
      const api = getApiClient();
      await api.put(API_ENDPOINTS.SUPER_ADMIN.PRE.CHECK_DEFINITIONS.UPDATE(id), dto);
      set({ isUpdating: false });
      await get().loadCheckDefinitions();
      return true;
    } catch (error) {
      set({
        isUpdating: false,
        actionError: {
          code: 'UPDATE_CHECK_DEFINITION_FAILED',
          message: error instanceof Error ? error.message : 'Failed to update check definition',
        },
      });
      return false;
    }
  },

  deleteCheckDefinition: async (id: string): Promise<boolean> => {
    set({ isDeleting: true, actionError: null });
    try {
      const api = getApiClient();
      await api.delete(API_ENDPOINTS.SUPER_ADMIN.PRE.CHECK_DEFINITIONS.DELETE(id));
      set((state) => ({
        checkDefinitions: state.checkDefinitions.filter((c) => c.id !== id),
        isDeleting: false,
      }));
      return true;
    } catch (error) {
      set({
        isDeleting: false,
        actionError: {
          code: 'DELETE_CHECK_DEFINITION_FAILED',
          message: error instanceof Error ? error.message : 'Failed to delete check definition',
        },
      });
      return false;
    }
  },

  // ============================================
  // Decision Log Actions
  // ============================================

  loadDecisionLogs: async (page = 1) => {
    set({ isLoadingDecisionLogs: true, decisionLogsError: null });
    try {
      const api = getApiClient();
      const { decisionLogFilters } = get();

      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', '20');
      if (decisionLogFilters.tenantId) params.set('tenantId', decisionLogFilters.tenantId);
      if (decisionLogFilters.channel) params.set('channel', decisionLogFilters.channel);
      if (decisionLogFilters.messagePurpose) params.set('messagePurpose', decisionLogFilters.messagePurpose);
      if (decisionLogFilters.decision) params.set('decision', decisionLogFilters.decision);
      if (decisionLogFilters.dateRange) params.set('dateRange', decisionLogFilters.dateRange);
      if (decisionLogFilters.internalDncOnly) params.set('internalDncOnly', 'true');

      const response = await api.get<{ data: PREDecisionLog[]; pagination: PaginationMeta }>(
        `${API_ENDPOINTS.SUPER_ADMIN.PRE.DECISIONS.LIST}?${params.toString()}`
      );

      set({
        decisionLogs: response.data,
        decisionLogsPagination: response.pagination,
        isLoadingDecisionLogs: false,
      });
    } catch {
      // Fallback to mock data with client-side filtering
      const { decisionLogFilters } = get();
      let filtered = [...MOCK_DECISION_LOGS];

      if (decisionLogFilters.channel) {
        filtered = filtered.filter((l) => l.channel === decisionLogFilters.channel);
      }
      if (decisionLogFilters.messagePurpose) {
        filtered = filtered.filter((l) => l.messagePurpose === decisionLogFilters.messagePurpose);
      }
      if (decisionLogFilters.decision) {
        filtered = filtered.filter((l) => l.decision === decisionLogFilters.decision);
      }

      set({
        decisionLogs: filtered,
        decisionLogsPagination: mockPagination(filtered.length, page),
        isLoadingDecisionLogs: false,
      });
    }
  },

  loadDecisionLog: async (id: string) => {
    set({ isLoadingDecisionLog: true });
    try {
      const api = getApiClient();
      const response = await api.get<PREDecisionLog>(API_ENDPOINTS.SUPER_ADMIN.PRE.DECISIONS.GET(id));
      set({ activeDecisionLog: response, isLoadingDecisionLog: false });
    } catch {
      const mockLog = MOCK_DECISION_LOGS.find((l) => l.id === id) ?? null;
      set({ activeDecisionLog: mockLog, isLoadingDecisionLog: false });
    }
  },

  setDecisionLogFilter: (key, value) => {
    set((state) => ({
      decisionLogFilters: { ...state.decisionLogFilters, [key]: value },
    }));
    get().loadDecisionLogs(1);
  },

  clearDecisionLogFilters: () => {
    set({ decisionLogFilters: {} });
    get().loadDecisionLogs(1);
  },

  // ============================================
  // AI Classification Actions
  // ============================================

  loadAiClassifications: async (page = 1) => {
    set({ isLoadingAiClassifications: true, aiClassificationsError: null });
    try {
      const api = getApiClient();
      const { aiClassificationFilters } = get();

      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', '20');
      if (aiClassificationFilters.classifiedPurpose) params.set('classifiedPurpose', aiClassificationFilters.classifiedPurpose);
      if (aiClassificationFilters.isFlagged !== undefined) params.set('isFlagged', String(aiClassificationFilters.isFlagged));
      if (aiClassificationFilters.dateRange) params.set('dateRange', aiClassificationFilters.dateRange);

      const response = await api.get<{ data: PREAiClassification[]; pagination: PaginationMeta }>(
        `${API_ENDPOINTS.SUPER_ADMIN.PRE.AI.LIST}?${params.toString()}`
      );

      set({
        aiClassifications: response.data,
        aiClassificationsPagination: response.pagination,
        isLoadingAiClassifications: false,
      });
    } catch {
      // Fallback to mock data with client-side filtering
      const { aiClassificationFilters } = get();
      let filtered = [...MOCK_AI_CLASSIFICATIONS];

      if (aiClassificationFilters.classifiedPurpose) {
        filtered = filtered.filter((c) => c.classifiedPurpose === aiClassificationFilters.classifiedPurpose);
      }
      if (aiClassificationFilters.isFlagged !== undefined) {
        filtered = filtered.filter((c) => c.isFlagged === aiClassificationFilters.isFlagged);
      }

      set({
        aiClassifications: filtered,
        aiClassificationsPagination: mockPagination(filtered.length, page),
        isLoadingAiClassifications: false,
      });
    }
  },

  overrideAiClassification: async (id: string, purpose: MessagePurpose): Promise<boolean> => {
    set({ isUpdating: true, actionError: null });
    try {
      const api = getApiClient();
      await api.patch(API_ENDPOINTS.SUPER_ADMIN.PRE.AI.OVERRIDE(id), { overriddenPurpose: purpose });
      set({ isUpdating: false });
      // Refresh list
      const { aiClassificationsPagination } = get();
      await get().loadAiClassifications(aiClassificationsPagination?.page ?? 1);
      return true;
    } catch (error) {
      set({
        isUpdating: false,
        actionError: {
          code: 'OVERRIDE_AI_CLASSIFICATION_FAILED',
          message: error instanceof Error ? error.message : 'Failed to override AI classification',
        },
      });
      return false;
    }
  },

  setAiClassificationFilter: (key, value) => {
    set((state) => ({
      aiClassificationFilters: { ...state.aiClassificationFilters, [key]: value },
    }));
    get().loadAiClassifications(1);
  },

  clearAiClassificationFilters: () => {
    set({ aiClassificationFilters: {} });
    get().loadAiClassifications(1);
  },

  // ============================================
  // Import/Export Actions
  // ============================================

  importRules: async (rules: CreateRuleDto[]): Promise<{ imported: number; failed: number } | null> => {
    set({ isCreating: true, actionError: null });
    try {
      const api = getApiClient();
      const response = await api.post<{ results: { ruleId: string; success: boolean; error?: string }[]; imported: number; failed: number }>(
        API_ENDPOINTS.SUPER_ADMIN.PRE.RULES.IMPORT,
        rules
      );
      set({ isCreating: false });
      await get().refreshRules();
      return { imported: response.imported, failed: response.failed };
    } catch (error) {
      set({
        isCreating: false,
        actionError: {
          code: 'IMPORT_RULES_FAILED',
          message: error instanceof Error ? error.message : 'Failed to import rules',
        },
      });
      return null;
    }
  },

  exportRules: async (jurisdictionCode?: string): Promise<Rule[]> => {
    try {
      const api = getApiClient();
      const params = jurisdictionCode ? `?jurisdictionCode=${jurisdictionCode}` : '';
      const response = await api.get<Rule[]>(`${API_ENDPOINTS.SUPER_ADMIN.PRE.RULES.EXPORT}${params}`);
      return response;
    } catch {
      return [...PRE_RULES];
    }
  },

  // ============================================
  // Stats Actions
  // ============================================

  decisionStats: null,
  isLoadingDecisionStats: false,

  loadDecisionStats: async () => {
    set({ isLoadingDecisionStats: true });
    try {
      const api = getApiClient();
      const response = await api.get<DecisionStats>(API_ENDPOINTS.SUPER_ADMIN.PRE.DECISIONS.STATS);
      set({ decisionStats: response, isLoadingDecisionStats: false });
    } catch {
      set({ decisionStats: null, isLoadingDecisionStats: false });
    }
  },

  aiStats: null,
  isLoadingAiStats: false,

  loadAiStats: async () => {
    set({ isLoadingAiStats: true });
    try {
      const api = getApiClient();
      const response = await api.get<AiClassificationStats>(API_ENDPOINTS.SUPER_ADMIN.PRE.AI.STATS);
      set({ aiStats: response, isLoadingAiStats: false });
    } catch {
      set({ aiStats: null, isLoadingAiStats: false });
    }
  },

  // ============================================
  // Utility Actions
  // ============================================

  clearError: () => {
    set({
      jurisdictionsError: null,
      rulesError: null,
      callingHoursError: null,
      dncRegistriesError: null,
      frequencyCapsError: null,
      decisionLogsError: null,
      aiClassificationsError: null,
      actionError: null,
    });
  },

  reset: () => {
    set(getInitialState());
  },
}));

// ============================================
// Selector Hooks
// ============================================

/** Get rules filtered by jurisdiction code */
export const useRulesByJurisdiction = (code: string) => {
  return usePolicyStore((state) =>
    state.rules.filter((r) => r.jurisdictionCode === code)
  );
};

/** Get count of active rules */
export const useActiveRulesCount = () => {
  return usePolicyStore((state) =>
    state.rules.filter((r) => r.isActive).length
  );
};

/** Get count of active jurisdictions */
export const useActiveJurisdictionCount = () => {
  return usePolicyStore((state) =>
    state.jurisdictions.filter((j) => j.isActive).length
  );
};

/** Get decision statistics */
export const useDecisionStats = () => {
  return usePolicyStore((state) => {
    const logs = state.decisionLogs;
    const total = logs.length;
    return {
      total,
      allowed: logs.filter((l) => l.decision === 'ALLOW').length,
      conditional: logs.filter((l) => l.decision === 'ALLOW_WITH_CONDITIONS').length,
      blocked: logs.filter((l) => l.decision === 'BLOCK').length,
    };
  });
};

/** Check if any policy data is loading */
export const usePolicyLoading = () => {
  const isLoadingJurisdictions = usePolicyStore((state) => state.isLoadingJurisdictions);
  const isLoadingRules = usePolicyStore((state) => state.isLoadingRules);
  const isCreating = usePolicyStore((state) => state.isCreating);
  const isUpdating = usePolicyStore((state) => state.isUpdating);
  const isDeleting = usePolicyStore((state) => state.isDeleting);
  const isToggling = usePolicyStore((state) => state.isTogglingRule);
  return isLoadingJurisdictions || isLoadingRules || isCreating || isUpdating || isDeleting || isToggling !== null;
};

/** Get AI classification override rate */
export const useAiOverrideRate = () => {
  return usePolicyStore((state) => {
    const total = state.aiClassifications.length;
    if (total === 0) return 0;
    const overridden = state.aiClassifications.filter((c) => c.overriddenPurpose !== null && c.overriddenPurpose !== undefined).length;
    return overridden / total;
  });
};

/** Get flagged AI classifications count */
export const useFlaggedClassificationsCount = () => {
  return usePolicyStore((state) =>
    state.aiClassifications.filter((c) => c.isFlagged).length
  );
};

/** Get active rule filters count */
export const useRuleFiltersCount = () => {
  return usePolicyStore((state) => {
    let count = 0;
    if (state.rulesFilter.search) count++;
    if (state.rulesFilter.jurisdictionCode) count++;
    if (state.rulesFilter.channel) count++;
    if (state.rulesFilter.messagePurpose) count++;
    if (state.rulesFilter.layer) count++;
    if (state.rulesFilter.isActive !== undefined) count++;
    return count;
  });
};
